
import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import TranslatedText from '@/TranslatedText';
import { useNavigate } from 'react-router-dom';
import { categoriesData, Category, SubCategory } from '@/data/categoriesData';

interface CategorySidebarProps {
  className?: string;
}

const CategorySidebar: React.FC<CategorySidebarProps> = ({
  className = ''
}) => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleCategory = (categoryName: string) => {
    setOpenCategory(prevOpen => 
      prevOpen === categoryName
        ? null
        : categoryName
    );
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategory === categoryName;
  };

  const handleSubcategoryClick = (subcategoryLink: string) => {
    navigate(subcategoryLink);
  };

  return (
    <aside className={`bg-white shadow-md flex flex-col h-full relative z-[150] ${className}`}>
      <div className="border-b border-gray-200">
        <div className="px-4 py-[5px]">
          <TranslatedText 
            text="CATEGORIES" 
            className="text-ahavex-blue text-lg font-bold"
          />
        </div>
      </div>
      
      <ScrollArea className="flex-grow">
        <div className="divide-y divide-gray-200">
          {categoriesData.map(category => (
            <Collapsible 
              key={category.id}
              open={isCategoryOpen(category.name)}
              onOpenChange={() => toggleCategory(category.name)}
            >
              <CollapsibleTrigger className="w-full">
                <div
                  className={`flex items-center justify-between py-3.5 px-4 hover:bg-gray-50 cursor-pointer ${
                    isCategoryOpen(category.name) ? 'bg-blue-50' : ''
                  }`}
                  aria-label={`Browse ${category.name} category`}
                >
                  <div className="flex items-center space-x-2">
                    <category.icon size={18} className="text-[#0EA5E9]" aria-hidden="true" />
                    <TranslatedText 
                      text={category.name} 
                      className="transition-transform duration-200"
                    />
                  </div>
                  {isCategoryOpen(category.name) ? 
                    <ChevronDown size={16} className="text-gray-400" aria-hidden="true" /> : 
                    <ChevronRight size={16} className="text-gray-400" aria-hidden="true" />
                  }
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pl-10 pr-4 pb-1 bg-gray-50">
                  {category.subcategories.map(subcategory => (
                    <div
                      key={subcategory.name}
                      className="block py-2.5 hover:text-ahavex-blue transition-colors duration-200 cursor-pointer"
                      onClick={() => handleSubcategoryClick(subcategory.link)}
                      aria-label={`Browse ${subcategory.name} in ${category.name}`}
                    >
                      <TranslatedText text={subcategory.name} />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </ScrollArea>
    </aside>
  );
};

export default CategorySidebar;
